/**
 * DOM-based column width measurement for EditableTable columns that use
 * `renderItemFn`.
 *
 * The canvas measurement in `measureColumnWidths.ts` reads
 * `String(item[label])`, which misses:
 *   - Date objects (typeof === 'object' → ignored)
 *   - Array values (same)
 *   - Anything renderItemFn adds: icons, badges, formatted strings,
 *     concatenations of multiple fields
 *
 * For renderItemFn columns we render each row's ReactNode output to an
 * HTML string via `renderToString`, mount all of those strings in a
 * single offscreen container (so the browser does ONE layout pass for
 * the entire batch), then read each rendered element's `scrollWidth`.
 *
 * Synchronous.  Per-row cost: one `renderToString` plus a DOM append.
 * Caller is expected to cache the result so this runs at most once per
 * data version per column set.
 */
import type {ReactNode} from 'react';
import {renderToString} from 'react-dom/server';

export interface RenderItemFnColumn<T> {
    label: string;
    width?: string | number;
    renderItemFn?: (item: T, label?: string, isExport?: boolean, width?: string | number) => unknown;
}

/**
 * Measure the natural rendered width of every row's `renderItemFn`
 * output for every column that has a `renderItemFn`.  Columns without
 * one are skipped (canvas measurement in `measureColumnWidths` handles
 * those).  Columns with an explicit `width` ARE measured — the caller
 * (`buildGridColumnTrack`) folds the measured value into the column's
 * sizing rule (e.g. as the MIN side of a `minmax(MIN, MAX)`).
 *
 * Returns a `{ label: pixelWidth }` map containing only the columns
 * actually measured.  Pixel values include the cell's horizontal
 * padding (taken from `sampleCell`).
 */
export function measureRenderItemFnWidths<T>(
    items: readonly T[],
    columns: readonly RenderItemFnColumn<T>[],
    sampleCell: HTMLElement,
    /**
     * The element under which to mount the offscreen container.
     * Anchoring inside the live DOM tree is critical: many `renderItemFn`
     * outputs rely on page-scoped CSS (e.g. Contacts' `.contact-list
     * .ResourceLine` rules) that DOES NOT cascade to a container parked
     * in `document.body`.  Mounting inside the grid (or one of its
     * ancestors) ensures the same selectors that style the live cells
     * also style the offscreen ones, so measured widths match real
     * rendered widths.  The container is `position: absolute` so it
     * doesn't disturb the live layout.
     */
    anchorEl?: HTMLElement,
): Record<string, number> {
    // Every `renderItemFn` column needs full-dataset measurement,
    // regardless of declared `width` — the downstream sizing rule (in
    // `buildGridColumnTrack`) consumes the measured value differently
    // depending on the declaration:
    //
    //   - intrinsic keyword (max-content / min-content / auto) or no
    //     declared width → exact pixel value `${m}px`
    //   - minmax(MIN, MAX) with length MIN → minmax MIN augmented to
    //     `max(MIN, ${m}px)`
    //   - fr units, fixed declarations (em, px, %), intrinsic-MIN
    //     minmax → declared verbatim (developer's choice respected)
    //
    // The offscreen pass below walks every row deterministically, so
    // it catches outliers that the body-cell DOM floor (visible rows
    // only) would miss for virtualized tables.
    const colsWithRender = columns.filter(c => !!c.renderItemFn);
    if (colsWithRender.length === 0 || items.length === 0) return {};

    const cs = getComputedStyle(sampleCell);

    const container = document.createElement('div');
    container.style.cssText = [
        'position:absolute', 'left:-99999px', 'top:0',
        'visibility:hidden', 'pointer-events:none',
        `font:${cs.font || `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`}`,
    ].join(';');

    const cellMap = new Map<string, HTMLElement[]>();
    for (const col of colsWithRender) {
        const cells: HTMLElement[] = [];
        for (const item of items) {
            let html: string;
            try {
                const node = col.renderItemFn!(item, col.label, false);
                if (node == null) continue;
                html = renderToString(node as ReactNode);
            } catch {
                // A renderItemFn that throws for a single row shouldn't
                // poison the entire column's measurement.  Skip that row.
                continue;
            }
            if (!html) continue;
            const cell = document.createElement('div');
            cell.style.cssText = [
                `padding-left:${cs.paddingLeft}`,
                `padding-right:${cs.paddingRight}`,
                'white-space:nowrap',
                'width:max-content',
                'display:block',
            ].join(';');
            cell.innerHTML = html;
            container.appendChild(cell);
            cells.push(cell);
        }
        cellMap.set(col.label, cells);
    }

    (anchorEl ?? document.body).appendChild(container);

    try {
        // Force one layout pass for the entire batch before reading widths
        // — sequential read/write/read/write would thrash layout per row.
        void container.offsetHeight;

        const result: Record<string, number> = {};
        for (const [label, cells] of cellMap) {
            let max = 0;
            for (const cell of cells) {
                if (cell.scrollWidth > max) max = cell.scrollWidth;
            }
            if (max > 0) result[label] = max;
        }
        return result;
    } finally {
        container.parentElement?.removeChild(container);
    }
}
